import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Scale, ChevronDown, ChevronRight, BookOpen, AlertTriangle } from "lucide-react";
import { verdictLabel, basisLabel, type DecisionEvaluation } from "@costadorada/shared";
import { cn } from "@/lib/cn";

const verdictStyle: Record<string, string> = {
  WELL_GROUNDED: "bg-green-100 text-green-800",
  MOSTLY_GROUNDED: "bg-teal-100 text-teal-800",
  SOME_CONCERNS: "bg-amber-100 text-amber-800",
  ARBITRARY_ELEMENTS: "bg-red-100 text-red-800",
};
const basisStyle: Record<string, string> = {
  RULE_BASED: "bg-green-100 text-green-800",
  DISCRETIONARY: "bg-amber-100 text-amber-800",
  UNSUPPORTED: "bg-red-100 text-red-800",
};

/** Impartial audit of the Junta's decision: per-condition rule-basis classification.
 *  Content authored in Spanish by the model; labels follow the app language. */
export function DecisionEvaluationPanel({ evaluation, defaultOpen = true }: { evaluation: DecisionEvaluation; defaultOpen?: boolean }) {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const L = (m: { es: string; en: string }) => (isEn ? m.en : m.es);
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/50">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-indigo-900">
          <Scale size={16} /> {tr("Evaluación imparcial de la decisión", "Impartial decision evaluation")}
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-bold", verdictStyle[evaluation.verdict])}>
            {L(verdictLabel[evaluation.verdict])}
          </span>
        </span>
        {open ? <ChevronDown size={16} className="text-indigo-700" /> : <ChevronRight size={16} className="text-indigo-700" />}
      </button>

      {open && (
        <div className="space-y-3 px-3 pb-3 text-sm">
          <p className="text-slate-700">{evaluation.summaryEs}</p>

          {evaluation.conditions.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-800">
                {tr("Condiciones evaluadas", "Conditions evaluated")}
              </div>
              <div className="space-y-1.5">
                {evaluation.conditions.map((c, i) => (
                  <div key={i} className="rounded border border-slate-200 bg-white px-2.5 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold", basisStyle[c.basis])}>
                        {L(basisLabel[c.basis])}
                      </span>
                      <span className="font-medium text-slate-800">{c.conditionEs}</span>
                    </div>
                    {c.assessmentEs && <div className="mt-1 text-[13px] text-slate-600">{c.assessmentEs}</div>}
                    {c.citation && (c.citation.source || c.citation.article) && (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <BookOpen size={12} className="shrink-0 text-primary" />
                        <span className="text-[12px] font-medium text-slate-700">{c.citation.source}</span>
                        {c.citation.article && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">{c.citation.article}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {evaluation.concerns.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-800">
                {tr("Puntos de atención", "Points of attention")}
              </div>
              <ul className="space-y-1">
                {evaluation.concerns.map((c, i) => (
                  <li key={i} className="flex gap-1.5 text-slate-700">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {evaluation.proceduralNotesEs && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-800">
                {tr("Notas de procedimiento", "Procedural notes")}
              </div>
              <p className="text-[13px] text-slate-600">{evaluation.proceduralNotesEs}</p>
            </div>
          )}

          <div className="border-t border-indigo-200 pt-2 text-[11px] text-slate-500">
            {evaluation.documentsRead.length > 0 && (
              <div>{tr("Documentos leídos", "Documents read")}: {evaluation.documentsRead.join(", ")}</div>
            )}
            <div>
              {evaluation.engine === "claude"
                ? tr(`Generado por Claude${evaluation.model ? ` (${evaluation.model})` : ""}`, `Generated by Claude${evaluation.model ? ` (${evaluation.model})` : ""}`)
                : tr("Generado por el motor de reglas (configure la clave de Claude para la evaluación imparcial)", "Generated by the rules engine (set the Claude key for the impartial evaluation)")}
            </div>
            <div className="mt-1 italic">{evaluation.disclaimerEs}</div>
          </div>
        </div>
      )}
    </div>
  );
}
